import { Filesystem, Directory } from '@capacitor/filesystem';
import { isNative } from './capacitorService';

const OFFLINE_DIR = 'offline_queue';

// Referensi foto yang disimpan — berbeda antara native dan browser
export type PhotoRef =
  | { type: 'path'; value: string; filename: string }
  | { type: 'base64'; value: string; filename: string; mimeType: string };

// ── Helpers ──────────────────────────────────────────────────────────────────

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string); // "data:image/jpeg;base64,..."
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function ensureDir(): Promise<void> {
  try {
    await Filesystem.mkdir({
      path: OFFLINE_DIR,
      directory: Directory.Data,
      recursive: true,
    });
  } catch {
    // Folder sudah ada — abaikan error
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Simpan foto untuk antrian offline.
 * Native  → file disimpan ke Capacitor Filesystem, kembalikan path.
 * Browser → file dikonversi ke base64, kembalikan string.
 */
export async function savePhotoForQueue(file: File): Promise<PhotoRef> {
  const filename = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;

  if (isNative) {
    await ensureDir();
    const base64 = await fileToBase64(file);
    // Hapus prefix "data:image/jpeg;base64," — Filesystem hanya mau data murni
    const base64Data = base64.split(',')[1];
    await Filesystem.writeFile({
      path: `${OFFLINE_DIR}/${filename}`,
      data: base64Data,
      directory: Directory.Data,
    });
    return { type: 'path', value: `${OFFLINE_DIR}/${filename}`, filename };
  } else {
    // Browser fallback: simpan sebagai base64 lengkap (dengan prefix)
    const base64 = await fileToBase64(file);
    return { type: 'base64', value: base64, filename, mimeType: file.type };
  }
}

/**
 * Muat kembali foto dari referensi menjadi File object (untuk diupload).
 */
export async function loadPhotoAsFile(ref: PhotoRef): Promise<File> {
  if (ref.type === 'path') {
    const result = await Filesystem.readFile({
      path: ref.value,
      directory: Directory.Data,
    });
    // result.data bisa string base64 atau Blob tergantung versi Capacitor
    let blob: Blob;
    if (typeof result.data === 'string') {
      const byteChars = atob(result.data);
      const byteArr = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
      blob = new Blob([byteArr], { type: 'image/jpeg' });
    } else {
      blob = result.data as unknown as Blob;
    }
    return new File([blob], ref.filename, { type: 'image/jpeg' });
  } else {
    // base64 dengan prefix "data:mime;base64,..."
    const [header, data] = ref.value.split(',');
    const mimeMatch = header.match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : ref.mimeType;
    const byteChars = atob(data);
    const byteArr = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
    const blob = new Blob([byteArr], { type: mime });
    return new File([blob], ref.filename, { type: mime });
  }
}

/**
 * Hapus file lokal setelah sync berhasil.
 */
export async function deleteQueuePhoto(ref: PhotoRef): Promise<void> {
  if (ref.type !== 'path') return; // base64 tidak ada file fisik
  try {
    await Filesystem.deleteFile({
      path: ref.value,
      directory: Directory.Data,
    });
  } catch {
    // File mungkin sudah terhapus — abaikan
  }
}

/**
 * Hapus semua file offline yang sudah selesai (cleanup massal).
 */
export async function clearAllQueuePhotos(): Promise<void> {
  if (!isNative) return;
  try {
    await Filesystem.rmdir({
      path: OFFLINE_DIR,
      directory: Directory.Data,
      recursive: true,
    });
  } catch {
    // Abaikan jika folder tidak ada
  }
}
